import { ComponentType, lazy, Suspense } from 'react'
import { TrayPopoverLayout } from '@/components/layout/TrayPopoverLayout'
import { TodayRedirect } from '@/components/organization/TodayRedirect'
// Import global settings pages
import SettingsPage from '@/pages/settings'
import McpSettingsPage from '@/pages/settings/mcp'
import TrayTasksPage from '@/pages/tray-tasks'
import { Navigate } from 'react-router-dom'

import {
  AppLoader,
  ClientPortalRouteShell,
  ProtectedLayout,
} from '../components'
import { AppRouteObject } from '../types'

const SettingsColumnsPage = lazy(() => import('@/pages/settings/columns'))
const SettingsTicketTypesPage = lazy(
  () => import('@/pages/settings/ticket-types'),
)

// Helper to create lazy routes
const lazyLoad = (
  importFn: () => Promise<
    { default: ComponentType<unknown> } | Record<string, ComponentType<unknown>>
  >,
  namedExport?: string,
) => {
  return {
    lazy: async () => {
      const module = await importFn()
      const Component = namedExport
        ? (module as Record<string, ComponentType<unknown>>)[namedExport]
        : (module as { default: ComponentType<unknown> }).default
      return { Component }
    },
  }
}

// Global app routes configuration (for routes not tied to a specific organization)
export const appRoutes: AppRouteObject[] = [
  // Root redirect
  {
    path: '/',
    element: <Navigate to="/organizations" replace />,
    title: 'Home',
  },
  // Tray popover routes (minimal layout for menu bar popover)
  {
    element: <TrayPopoverLayout />,
    children: [
      {
        path: '/tray-tasks',
        element: <TrayTasksPage />,
        title: 'Quick Tasks',
      },
    ],
  },
  // Magic-link portal — public, no auth required at the React-Router level.
  // The verify page exchanges the URL token for an HttpOnly cookie, then
  // the ticket page rides that cookie for all subsequent API calls.
  {
    path: '/portal/m/:token',
    ...lazyLoad(() => import('@/pages/portal-magic/verify')),
    title: 'Opening Ticket',
  },
  {
    path: '/portal/ticket/:ticketId',
    ...lazyLoad(() => import('@/pages/portal-magic/ticket')),
    title: 'Your Ticket',
  },
  // Public unsubscribe landing page from outbound List-Unsubscribe link.
  {
    path: '/unsubscribe/:token',
    ...lazyLoad(() => import('@/pages/unsubscribe')),
    title: 'Unsubscribe',
  },
  {
    element: <ClientPortalRouteShell />,
    children: [
      {
        path: '/client-portal/:entityId',
        ...lazyLoad(() => import('@/pages/client-portal')),
        title: 'Client Portal',
      },
      {
        path: '/client-portal/:entityId/projects/:projectId',
        ...lazyLoad(() => import('@/pages/client-portal/project')),
        title: 'Project - Client Portal',
      },
      {
        path: '/client-portal/:entityId/projects/:projectId/:projectTab',
        ...lazyLoad(() => import('@/pages/client-portal/project')),
        title: 'Project - Client Portal',
      },
      {
        path: '/client-portal/:entityId/notes/:noteId',
        ...lazyLoad(() => import('@/pages/client-portal/note')),
        title: 'Note - Client Portal',
      },
      {
        path: '/client-portal/:entityId/threads/:threadId',
        ...lazyLoad(() => import('@/pages/client-portal/thread')),
        title: 'Discussion - Client Portal',
      },
      {
        path: '/client-portal/:entityId/docs',
        ...lazyLoad(() => import('@/pages/client-portal/docs')),
        title: 'Notes - Client Portal',
      },
      {
        path: '/client-portal/:entityId/links',
        ...lazyLoad(() => import('@/pages/client-portal/links')),
        title: 'Documents & Links - Client Portal',
      },
      {
        path: '/client-portal/:entityId/updates',
        ...lazyLoad(() => import('@/pages/client-portal/updates')),
        title: 'Updates - Client Portal',
      },
      {
        path: '/client-portal/:entityId/contract-usage',
        ...lazyLoad(() => import('@/pages/client-portal/contract-usage')),
        title: 'Contract Usage - Client Portal',
      },
      {
        path: '/client-portal/:entityId/deployments',
        ...lazyLoad(() => import('@/pages/client-portal/deployments')),
        title: 'Deployments - Client Portal',
      },
      {
        path: '/client-portal/:entityId/tickets',
        ...lazyLoad(() => import('@/pages/client-portal/tickets')),
        title: 'Support - Client Portal',
      },
      {
        path: '/client-portal/:entityId/tickets/search',
        ...lazyLoad(() => import('@/pages/client-portal/ticket-search')),
        title: 'Search Tickets - Client Portal',
      },
      {
        path: '/client-portal/:entityId/tickets/:ticketId',
        ...lazyLoad(() => import('@/pages/client-portal/ticket')),
        title: 'Ticket - Client Portal',
      },
      {
        path: '/client-portal/:entityId/settings',
        ...lazyLoad(() => import('@/pages/client-portal/settings')),
        title: 'My Profile - Client Portal',
      },
    ],
  },
  // Protected routes not tied to a specific organization
  {
    element: <ProtectedLayout />,
    children: [
      // User settings
      {
        path: '/settings',
        element: <SettingsPage />,
        title: 'Settings',
      },
      {
        path: '/settings/columns',
        element: (
          <Suspense fallback={<AppLoader />}>
            <SettingsColumnsPage />
          </Suspense>
        ),
        title: 'Columns Settings',
      },
      {
        path: '/settings/ticket-types',
        element: (
          <Suspense fallback={<AppLoader />}>
            <SettingsTicketTypesPage />
          </Suspense>
        ),
        title: 'Ticket Types',
      },
      {
        path: '/settings/mcp',
        element: (
          <Suspense fallback={<AppLoader />}>
            <McpSettingsPage />
          </Suspense>
        ),
        title: 'AI Assistant Integration',
      },
      {
        path: '/help',
        ...lazyLoad(() => import('@/pages/help')),
        title: 'Help Center',
      },
      {
        path: '/whats-new',
        ...lazyLoad(() => import('@/pages/whats-new')),
        title: "What's New",
      },
      {
        path: '/help/search',
        ...lazyLoad(() => import('@/pages/help/search')),
        title: 'Search Help',
      },
      {
        path: '/help/categories/:categorySlug',
        ...lazyLoad(() => import('@/pages/help/category')),
        title: 'Help Category',
      },
      {
        path: '/help/articles/:helpId',
        ...lazyLoad(() => import('@/pages/help/article')),
        title: 'Help Article',
      },
      // Delete account
      {
        path: '/delete-account',
        ...lazyLoad(() => import('@/pages/delete-account')),
        title: 'Delete Account',
      },
      // Today redirect
      {
        path: '/today',
        element: <TodayRedirect />,
        title: 'Today',
      },
    ],
  },
]
